import React from "react";
import {Box, Button, Divider, Drawer, IconButton, Menu, MenuItem, Typography} from "@mui/material";
import {BlockOutlined, CloseOutlined, ReplayOutlined, TrendingUpOutlined} from "@mui/icons-material";
import type {PtSupplier} from "../paymentTermsDemoData";
import {PT_KPIS, PT_POLICY} from "../paymentTermsDemoData";
import {
    buildTermLookup,
    EXCLUSION_REASONS,
    formatCompactCurrency,
    formatFullCurrency,
    gapColor,
    recommendActions,
    termDaysLabel,
    termEffectiveDays,
    tierOf,
} from "../paymentTermsUtils";
import {usePaymentTermsMappingQuery} from "../usePaymentTermsQueries";
import {TierPill} from "./PaymentTermsCells";

type Props = {
    supplier: PtSupplier | null;
    exclusionReason?: string;
    onExclude: (supplier: PtSupplier, reason: string) => void;
    onRequalify: (supplier: PtSupplier) => void;
    onClose: () => void;
};

const Stat: React.FC<{label: string; value: React.ReactNode; hint?: string}> = ({label, value, hint}) => (
    <Box className="pt-drawer-stat">
        <Typography className="pt-drawer-stat-label">{label}</Typography>
        <Typography className="pt-drawer-stat-value">{value}</Typography>
        {hint && <Typography className="pt-drawer-stat-hint">{hint}</Typography>}
    </Box>
);

export const SupplierTermsDrawer: React.FC<Props> = ({supplier, exclusionReason, onExclude, onRequalify, onClose}) => {
    const s = supplier;
    const currency = PT_KPIS.currency;
    const [reasonAnchor, setReasonAnchor] = React.useState<HTMLElement | null>(null);
    const {data: mapping = []} = usePaymentTermsMappingQuery();
    const termLookup = React.useMemo(() => buildTermLookup(mapping), [mapping]);

    const excluded = !!exclusionReason;

    // Highlight the supplier's best (longest) already-agreed term.
    const bestDays = React.useMemo(() => {
        if (!s) return null;
        const days = s.terms.map(t => termEffectiveDays(t, termLookup)).filter((d): d is number => d !== null);
        return days.length ? Math.max(...days) : null;
    }, [s, termLookup]);

    return (
        // The app header is pinned at theme.zIndex.drawer + 1, so lift above it
        <Drawer anchor="right" open={!!s} onClose={onClose}
                sx={theme => ({zIndex: theme.zIndex.drawer + 2})}>
            {s && <Box className="pt-drawer">
                <Box className="pt-drawer-header">
                    <Box>
                        <Typography variant="h2" className="pt-drawer-title">{s.supplier}</Typography>
                        <Box className="pt-drawer-subrow">
                            <TierPill tier={tierOf(s)}/>
                            <span className="pt-drawer-subtext">
                                {s.invoiceCount.toLocaleString('en-US')} invoices · {formatCompactCurrency(s.spend, currency)} spend (12m)
                            </span>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small"><CloseOutlined/></IconButton>
                </Box>

                {excluded && (
                    <Box className="pt-drawer-excluded-banner">
                        Excluded from the opportunity — {exclusionReason}. Its value is not counted in the totals.
                    </Box>
                )}

                <Box className={`pt-drawer-opportunity${excluded ? ' is-excluded' : ''}`}>
                    <TrendingUpOutlined/>
                    <Box>
                        <Typography className="pt-drawer-opportunity-value">
                            {s.quickWinOpportunity > 0 && s.quickWinOpportunity < s.wcOpportunity
                                ? `${formatCompactCurrency(s.quickWinOpportunity, currency)} – ${formatFullCurrency(s.wcOpportunity, currency)}`
                                : formatFullCurrency(s.wcOpportunity, currency)}
                        </Typography>
                        <Typography className="pt-drawer-opportunity-label">
                            {s.quickWinOpportunity > 0 && s.quickWinOpportunity < s.wcOpportunity
                                ? `Quick win at best existing term (${s.bestExistingTerm?.toFixed(0)}d) → full potential at ${Math.round(s.targetTerm)}-day policy`
                                : `Working capital release at ${Math.round(s.targetTerm)}-day terms`}
                        </Typography>
                    </Box>
                </Box>

                <Typography className="pt-drawer-formula">
                    {formatCompactCurrency(s.spend, currency)} spend × {s.gapDays.toFixed(1)} gap days ÷ 365
                    &nbsp;=&nbsp; {formatCompactCurrency(s.wcOpportunity, currency)} cash released
                </Typography>

                <Divider sx={{my: 2}}/>

                <Typography className="pt-drawer-section">Term profile</Typography>
                <Box className="pt-drawer-gauge">
                    <Box className="pt-drawer-gauge-track">
                        <Box className="pt-drawer-gauge-fill" style={{
                            width: `${Math.max(0, Math.min(1, s.currentTerm / Math.max(1, s.targetTerm))) * 100}%`,
                            backgroundColor: gapColor(s.gapDays),
                        }}/>
                    </Box>
                    <Box className="pt-drawer-gauge-legend">
                        <span>Current: <b>{s.currentTerm.toFixed(1)} days</b> (spend-weighted)</span>
                        <span>Target: <b>{s.targetTerm.toFixed(0)} days</b></span>
                    </Box>
                </Box>

                <Box className="pt-drawer-stats">
                    <Stat label="Immediate spend" value={formatCompactCurrency(s.immediateSpend, currency)}
                          hint="Paid on 0-day terms"/>
                    <Stat label="Below-target spend" value={formatCompactCurrency(s.belowTargetSpend, currency)}
                          hint={`Terms shorter than ${PT_POLICY.targetTermDays} days`}/>
                    <Stat label="Posted after due" value={formatCompactCurrency(s.postedAfterDueSpend, currency)}
                          hint="AP process risk"/>
                    <Stat label="Discount at risk" value={formatCompactCurrency(s.discountAtRiskSpend, currency)}
                          hint="Posted after discount window"/>
                </Box>

                {s.terms.length > 0 && <>
                    <Typography className="pt-drawer-section">
                        Payment terms in use ({s.distinctTermCount})
                    </Typography>
                    <Box className="pt-drawer-terms">
                        {s.terms.map(t => {
                            const days = termDaysLabel(t, termLookup);
                            const isBest = bestDays !== null && termEffectiveDays(t, termLookup) === bestDays && s.multipleTerms;
                            return (
                                <span key={t} className={`pt-term-chip${isBest ? ' is-best' : ''}`}>
                                    {t}
                                    {days && <span className="pt-term-chip-days">{days}</span>}
                                    {isBest && <span className="pt-term-chip-best">best</span>}
                                </span>
                            );
                        })}
                        {s.distinctTermCount > s.terms.length &&
                            <span className="pt-term-chip pt-term-chip-more">
                                +{s.distinctTermCount - s.terms.length} more
                            </span>
                        }
                    </Box>
                </>}

                <Typography className="pt-drawer-section">Recommended actions</Typography>
                <Box className="pt-drawer-recs">
                    {recommendActions(s, s.targetTerm).map(rec => (
                        <Box key={rec.title} className="pt-drawer-rec">
                            <Typography className="pt-drawer-rec-title">{rec.title}</Typography>
                            <Typography className="pt-drawer-rec-detail">{rec.detail}</Typography>
                        </Box>
                    ))}
                </Box>

                <Divider sx={{my: 2}}/>

                <Box className="pt-drawer-review">
                    {excluded
                        ? <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ReplayOutlined/>}
                            onClick={() => onRequalify(s)}>
                            Requalify as opportunity
                        </Button>
                        : <>
                            <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                startIcon={<BlockOutlined/>}
                                onClick={e => setReasonAnchor(e.currentTarget)}>
                                Disqualify from opportunity
                            </Button>
                            <span className="pt-drawer-review-hint">
                                For suppliers where terms cannot be renegotiated — the totals update accordingly.
                            </span>
                        </>
                    }
                    <Menu
                        anchorEl={reasonAnchor}
                        open={!!reasonAnchor}
                        onClose={() => setReasonAnchor(null)}>
                        {EXCLUSION_REASONS.map(reason => (
                            <MenuItem key={reason} onClick={() => {
                                setReasonAnchor(null);
                                onExclude(s, reason);
                            }}>
                                {reason}
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>
            </Box>}
        </Drawer>
    );
};
